// Alternative menu styles (Dark minimal / Tiles / Icon rail). They only draw the list of
// items they are given: Header.js passes the already access-filtered menu, so what a user
// can open is decided in exactly one place and these styles cannot widen it.
import React, { useState, useRef, useLayoutEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Avatar } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupsIcon from '@mui/icons-material/Groups';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import EngineeringIcon from '@mui/icons-material/Engineering';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import BuildIcon from '@mui/icons-material/Build';
import FactCheckIcon from '@mui/icons-material/FactCheck';

// Which section each menu item is listed under. Anything not listed falls under "Tools".
const SECTION_OF = {
  dashboard: 'Overview', 'follow up': 'Overview',
  members: 'Members', offers: 'Members', 'due users': 'Members', packages: 'Members', pt: 'Members',
  balance: 'Money', expense: 'Money',
  attendance: 'Staff', staff: 'Staff', holidays: 'Staff',
  diet: 'Training', exercise: 'Training',
  'operation logs': 'Logs', 'device logs': 'Logs', 'message logs': 'Logs', 'cron logs': 'Logs',
};
const SECTIONS = ['Overview', 'Members', 'Money', 'Staff', 'Training', 'Tools', 'Logs'];
const SECTION_ICON = {
  Overview: DashboardIcon, Members: GroupsIcon, Money: AccountBalanceIcon, Staff: EngineeringIcon,
  Training: FitnessCenterIcon, Tools: BuildIcon, Logs: FactCheckIcon,
};

// Every page mounts its own Header, so the menu is rebuilt on each click; remember where each list
// was scrolled and put it back, otherwise it jumps to the top.
const savedScroll = {};
const useKeepScroll = (key) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current && savedScroll[key]) ref.current.scrollTop = savedScroll[key];
  }, [key]);
  const onScroll = (e) => { savedScroll[key] = e.currentTarget.scrollTop; };
  return { ref, onScroll };
};

const groupItems = (items) => {
  const groups = {};
  items.forEach((item) => {
    const s = SECTION_OF[item.name] || 'Tools';
    (groups[s] = groups[s] || []).push(item);
  });
  return SECTIONS.filter((s) => groups[s]).map((s) => ({ name: s, items: groups[s] }));
};

const label = (item) => item.name.replace(/ logs?$/, (m) => (SECTION_OF[item.name] === 'Logs' ? '' : m));
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const UserBlock = ({ user, initial, dark, accent }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
    <Avatar sx={{ width: 36, height: 36, bgcolor: accent, color: dark ? '#1f1f1e' : '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
      {initial}
    </Avatar>
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2, color: dark ? '#f3f3f1' : 'inherit' }}>
        {user?.name || user?.username || 'User'}
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', opacity: 0.6, color: dark ? '#f3f3f1' : 'inherit' }}>
        {user?.role ? cap(user.role) : ''}
      </Typography>
    </Box>
  </Box>
);

// A · Dark minimal: sections with small headings, lime highlight on the open page.
const DarkMinimal = ({ items, isActive, onOpen, onLogout, user, initial }) => {
  const accent = '#9bc55a';
  const keep = useKeepScroll('dark');
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#2b2b2a', color: '#e8e8e5' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <UserBlock user={user} initial={initial} dark accent={accent} />
      </Box>
      <Box {...keep} sx={{ flex: 1, overflowY: 'auto', p: 1.25 }}>
        {groupItems(items).map((g) => (
          <Box key={g.name} sx={{ mb: 1.5 }}>
            <Typography sx={{ px: 1.25, py: 0.5, fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.5 }}>
              {g.name}
            </Typography>
            {g.items.map((item) => {
              const active = isActive(item.routes);
              return (
                <Box
                  key={item.name}
                  onClick={() => onOpen(item)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 0.9, mb: 0.25, borderRadius: '10px', cursor: 'pointer',
                    bgcolor: active ? accent : 'transparent', color: active ? '#1f1f1e' : '#e8e8e5', fontWeight: active ? 700 : 500,
                    '&:hover': { bgcolor: active ? accent : 'rgba(255,255,255,0.08)' },
                    '& svg': { fontSize: '1.15rem' },
                  }}
                >
                  {item.icon}
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 'inherit', textTransform: 'capitalize' }}>{label(item)}</Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
      <Box
        onClick={onLogout}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.25, py: 1.5, cursor: 'pointer', color: '#f08a80', borderTop: '1px solid rgba(255,255,255,0.08)', '&:hover': { bgcolor: 'rgba(240,138,128,0.1)' } }}
      >
        <LogoutIcon sx={{ fontSize: '1.15rem' }} />
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Log out</Typography>
      </Box>
    </Box>
  );
};

// B · Tiles: every page is a big tap target in a grid.
const Tiles = ({ items, isActive, onOpen, onLogout, user, initial, colors, dark }) => {
  const bg = dark ? '#1a1a1a' : '#ffffff';
  const fg = dark ? '#f1f5f9' : '#1B2420';
  const keep = useKeepScroll('tiles');
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: bg, color: fg }}>
      <Box sx={{ m: 1.25, p: 1.5, borderRadius: '14px', bgcolor: dark ? 'rgba(255,255,255,0.06)' : colors.leafSoft }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: dark ? '#f1f5f9' : colors.leaf }}>Maa Tara Tarini Gym</Typography>
        <Typography sx={{ fontSize: '0.72rem', opacity: 0.7 }}>
          {(user?.name || user?.username || 'User')}{user?.role ? ` · ${cap(user.role)}` : ''}
        </Typography>
      </Box>
      <Box {...keep} sx={{ flex: 1, overflowY: 'auto', px: 1.25, pb: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          {items.map((item) => {
            const active = isActive(item.routes);
            return (
              <Box
                key={item.name}
                onClick={() => onOpen(item)}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, py: 1.4, px: 0.5,
                  borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                  bgcolor: active ? colors.leaf : dark ? 'rgba(255,255,255,0.05)' : '#f6f5f0',
                  color: active ? '#fff' : fg,
                  border: `1px solid ${active ? colors.leaf : dark ? 'rgba(255,255,255,0.08)' : '#e6e4dc'}`,
                  '&:hover': { bgcolor: active ? colors.leaf : dark ? 'rgba(255,255,255,0.1)' : colors.leafSoft },
                }}
              >
                {item.icon}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: active ? 700 : 500, textTransform: 'capitalize', lineHeight: 1.15 }}>
                  {item.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box
        onClick={onLogout}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 1.4, cursor: 'pointer', color: '#C0392B', borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : '#e6e4dc'}` }}
      >
        <LogoutIcon sx={{ fontSize: '1.1rem' }} />
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Log out</Typography>
      </Box>
    </Box>
  );
};

// C · Icon rail: one icon per section on the left, that section's pages on the right.
const IconRail = ({ items, isActive, onOpen, onLogout, colors, dark }) => {
  const groups = groupItems(items);
  const activeGroup = (groups.find((g) => g.items.some((i) => isActive(i.routes))) || groups[0] || {}).name;
  const [picked, setPicked] = useState(null);
  const keep = useKeepScroll('rail');
  const current = groups.find((g) => g.name === (picked || activeGroup)) || groups[0];
  const bg = dark ? '#1a1a1a' : '#ffffff';
  const fg = dark ? '#f1f5f9' : '#1B2420';
  return (
    <Box sx={{ height: '100%', display: 'flex', bgcolor: bg, color: fg }}>
      <Box sx={{ width: 62, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5, gap: 0.75, bgcolor: dark ? 'rgba(255,255,255,0.04)' : '#f6f5f0', borderRight: `1px solid ${dark ? 'rgba(255,255,255,0.08)' : '#e6e4dc'}` }}>
        {groups.map((g) => {
          const Icon = SECTION_ICON[g.name] || BuildIcon;
          const on = current && current.name === g.name;
          return (
            <Tooltip key={g.name} title={g.name} placement="right">
              <IconButton
                onClick={() => setPicked(g.name)}
                sx={{ color: on ? '#fff' : fg, bgcolor: on ? colors.leaf : 'transparent', borderRadius: '12px', '&:hover': { bgcolor: on ? colors.leaf : colors.leafSoft } }}
              >
                <Icon />
              </IconButton>
            </Tooltip>
          );
        })}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Log out" placement="right">
          <IconButton onClick={onLogout} sx={{ color: '#C0392B' }}><LogoutIcon /></IconButton>
        </Tooltip>
      </Box>
      <Box {...keep} sx={{ flex: 1, minWidth: 0, p: 1.5, overflowY: 'auto' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.25 }}>{current ? current.name : ''}</Typography>
        <Typography sx={{ fontSize: '0.7rem', opacity: 0.6, mb: 1.25 }}>
          {current ? `${current.items.length} page${current.items.length === 1 ? '' : 's'}` : ''}
        </Typography>
        {current && current.items.map((item) => {
          const active = isActive(item.routes);
          return (
            <Box
              key={item.name}
              onClick={() => onOpen(item)}
              sx={{
                px: 1.25, py: 0.9, mb: 0.4, borderRadius: '10px', cursor: 'pointer', fontSize: '0.85rem',
                fontWeight: active ? 700 : 500, textTransform: 'capitalize',
                bgcolor: active ? (dark ? 'rgba(255,255,255,0.12)' : colors.leafSoft) : 'transparent',
                color: active && !dark ? colors.leaf : fg,
                '&:hover': { bgcolor: dark ? 'rgba(255,255,255,0.08)' : colors.leafSoft },
              }}
            >
              {label(item)}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default function NavVariant({ variant, ...props }) {
  if (variant === 'dark') return <DarkMinimal {...props} />;
  if (variant === 'tiles') return <Tiles {...props} />;
  return <IconRail {...props} />;
}
